import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import DatePicker from 'react-datepicker';
import Dropdown from '../../components/Dropdown';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const TakeActionModal = ({ lead, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState([]);
  const [actionOutcome, setActionOutcome] = useState('Completed');
  const [actionDate, setActionDate] = useState(null);
  const [actionTime, setActionTime] = useState('');
  const [actionDaysToAdd, setActionDaysToAdd] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [nextStep, setNextStep] = useState('Complete');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const nextStepOptions = [{value: 'On Hold', label: 'On Hold'}, {value: 'Reschedule', label: 'Reschedule'}, {value: 'Complete', label: 'Complete'}, {value: 'Lost', label: 'Lost'}];

  
  useEffect(() => {
    api.get('/masters/status').then(res => {
      setStatuses(res.data.filter(s => s.status === 'Active'));
    }).catch(console.error);
  }, []);

  const dropdownOptions = React.useMemo(() => {
    let opts = statuses.map(s => ({ value: s.name, label: s.name }));
    let scheduledFollowup = null;
    
    if (lead?.followups && lead.followups.length > 0) {
      const valid = lead.followups.filter(f => f.type !== 'System' && (!/^\[.*?\]/.test(f.notes || '') || f.notes?.startsWith('[Reschedule]')));
      if (valid.length > 0) {
        valid.sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        scheduledFollowup = valid[0]; // newest
      }
    }
    
    const isScheduleStatus = lead?.status?.toLowerCase() === 'schedule' || 
                             lead?.status?.toLowerCase() === 'reschedule' || 
                             lead?.status?.name?.toLowerCase() === 'schedule' || 
                             lead?.status?.name?.toLowerCase() === 'reschedule';

    if (isScheduleStatus) {
      const isStarted = scheduledFollowup && (scheduledFollowup.type === 'Visit Started' || scheduledFollowup.notes?.startsWith('[Start Visit]') || scheduledFollowup.notes?.startsWith('Visit Started') || scheduledFollowup.notes?.startsWith('[Check-In]'));
      if (isStarted) {
        opts = [
          { value: 'Complete Visit', label: 'Complete Visit' },
          { value: 'Cancel Visit', label: 'Cancel Visit' },
        ];
      } else {
        opts = [
          { value: 'Start Visit', label: 'Start Visit' },
          { value: 'Cancel Visit', label: 'Cancel Visit' },
        ];
      }
    }
    return opts;
  
  }, [statuses, lead]);

  useEffect(() => {
    if (dropdownOptions.length > 0) {
      if (!dropdownOptions.find(o => o.value === actionOutcome)) {
        setActionOutcome(dropdownOptions[0].value);
      }
    }
  }, [dropdownOptions, actionOutcome]);

  const handleActionDaysChange = (val) => {
    setActionDaysToAdd(val);
    if (val && !isNaN(val)) {
      const d = new Date();
      d.setDate(d.getDate() + parseInt(val, 10));
      setActionDate(d);
    } else {
      setActionDate(null);
    }
  };

  const handleActionDateChange = (date) => {
    setActionDate(date);
    if (date) {
      const today = new Date();
      today.setHours(0,0,0,0);
      const sel = new Date(date);
      sel.setHours(0,0,0,0);
      const diffTime = Math.abs(sel - today);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setActionDaysToAdd(diffDays.toString());
    } else {
      setActionDaysToAdd('');
    }
  };

  const getTimeDate = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':');
    const d = new Date();
    d.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
    return d;
  };

    const getFollowupType = (outcome, nextStep) => {
    if (!outcome) return 'Activity';
    if (outcome === 'Start Visit') return 'Visit Started';
    if (outcome === 'Complete Visit') return 'Visit Completed';
    if (outcome === 'Cancel Visit') return 'Visit Cancelled';
    if (outcome.toLowerCase().includes('reschedule') || (nextStep || '').toLowerCase() === 'reschedule') return 'Follow-up Rescheduled';
    if (outcome.toLowerCase().includes('schedule') || (nextStep || '').toLowerCase() === 'schedule') return 'Follow-up Scheduled';
    if (['won', 'completed', 'complete'].includes(outcome.toLowerCase())) return 'Lead is Complete';
    return outcome;
  };

  const handleActionSubmit = async (e) => {
    e.preventDefault();
    if (!lead) return;
    
    const requiresDate = actionOutcome?.toLowerCase()?.includes('schedule') || actionOutcome?.toLowerCase()?.includes('follow') || ((actionOutcome === 'Complete Visit' || actionOutcome === 'Cancel Visit') && nextStep === 'Reschedule');
    if (requiresDate && (!actionDate || !actionTime)) {
      toast.error("Please select a date and time for the follow-up");
      return;
    }
    
    try {
      let targetFollowup = null;
      let isTerminal = ['complete', 'completed', 'closed', 'lost', 'complete visit', 'cancel visit'].includes(actionOutcome?.toLowerCase());
      if ((actionOutcome === 'Complete Visit' || actionOutcome === 'Cancel Visit') && nextStep === 'Reschedule') isTerminal = false; // reschedule is not terminal
  
      
      if (lead.followups && lead.followups.length > 0) {
        const valid = lead.followups.filter(f => f.type !== 'System' && (!/^\[.*?\]/.test(f.notes || '') || f.notes?.startsWith('[Reschedule]')));
        if (valid.length > 0) {
          valid.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
          targetFollowup = valid[0];
        } else {
          const nonSystem = lead.followups.filter(f => f.type !== 'System');
          if (nonSystem.length > 0) {
            nonSystem.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
            targetFollowup = nonSystem[0];
          }
        }
      }
      
      if (targetFollowup) {
        const today = new Date();
        today.setHours(0,0,0,0);
        const scheduledDate = new Date(targetFollowup.date);
        scheduledDate.setHours(0,0,0,0);
        const isEarly = today < scheduledDate;

        // Close the original log without changing its date to preserve history
        await api.put(`/followups/${targetFollowup.id}`, {
          notes: targetFollowup.notes,
          type: 'Follow-up Closed',
          date: targetFollowup.date
        });
        
        let newNotes = `[${actionOutcome}] ${actionNotes}`;
        if (actionOutcome === 'Start Visit') {
          newNotes = (isEarly ? '(Started before schedule) ' : '') + actionNotes;
        }
        
        if (!actionOutcome?.toLowerCase()?.includes('schedule') && !actionOutcome?.toLowerCase()?.includes('follow')) {
          await api.post('/followups', {
            leadId: lead.id,
            date: new Date().toISOString(),
            notes: newNotes,
            type: getFollowupType(actionOutcome, nextStep),
            
          });
        }
      } else {
        if (actionOutcome === 'Start Visit') {
          await api.post('/followups', {
            leadId: lead.id,
            date: new Date().toISOString(),
            notes: actionNotes,
            type: 'Visit Started',
            
          });
        } else if (!actionOutcome?.toLowerCase()?.includes('schedule') && !actionOutcome?.toLowerCase()?.includes('follow')) {
          await api.post('/followups', {
            leadId: lead.id,
            date: new Date().toISOString(),
            notes: actionNotes,
            type: getFollowupType(actionOutcome, nextStep),
            
          });
        }
      }
      
      const finalOutcome = (actionOutcome === 'Complete Visit' || actionOutcome === 'Cancel Visit') ? nextStep : actionOutcome;
      
      if (isTerminal && !requiresDate) {
        await api.post('/followups', {
          leadId: lead.id,
          date: new Date().toISOString(),
          notes: '',
          type: getFollowupType(finalOutcome, nextStep),
          
        });
      }
      
      if (requiresDate) {
        const d = new Date(actionDate);
        const [hours, minutes] = actionTime.split(':');
        d.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        
        const isResched = (actionOutcome || '').toLowerCase().includes('reschedule') || (nextStep || '').toLowerCase() === 'reschedule';
        
        await api.post('/followups', {
          leadId: lead.id,
          date: d.toISOString(),
          notes: actionNotes || '',
          type: getFollowupType(actionOutcome, nextStep),
          
        });
      }
      
      const targetStatus = statuses.find(s => s.name.toLowerCase() === finalOutcome.toLowerCase());
      
      if (targetStatus) {
        const isConverted = ['won', 'completed', 'complete'].includes(targetStatus.name.toLowerCase());
        await api.put(`/leads/${lead.id}`, { 
          statusId: targetStatus.id,
          isCustomer: isConverted,
          skipAutoCancel: true
        });
        
        if (isConverted && lead.status !== targetStatus.name) {
          const { notifyAdmins } = await import('../../utils/notify');
          await notifyAdmins(`Lead #${lead.id} (${lead.name || lead.companyName}) was converted to a customer by ${user.name}.`);
        }
      }
      
      toast.success("Action saved successfully");
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save action");
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 2000 }}>
      <div className="modal-content fade-in" style={{ maxWidth: '700px', padding: '2rem' }}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-main">Take Action</h3>
          <button className="btn btn-secondary p-2" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleActionSubmit}>
          <div className="mb-4">
            <label className="text-sm font-semibold mb-2 block text-muted">Action Outcome</label>
            <Dropdown 
              style={{ width: '100%', borderRadius: '12px' }}
              value={actionOutcome}
              onChange={(val) => setActionOutcome(val)}
              options={dropdownOptions}
            />
          </div>
          
          {(actionOutcome === 'Complete Visit' || actionOutcome === 'Cancel Visit') && (
            <div className="mb-6">
              <label className="text-sm font-semibold mb-2 block text-muted" style={{ color: '#0369a1' }}>
                {actionOutcome === 'Complete Visit' ? 'Next Step (After Visit)' : 'Next Step (After Cancel)'}
              </label>
              <Dropdown 
                style={{ width: '100%', borderRadius: '12px' }}
                value={nextStep}
                onChange={(val) => setNextStep(val)}
                options={nextStepOptions}
              />
            </div>
          )}
          
          {((actionOutcome?.toLowerCase()?.includes('schedule') || actionOutcome?.toLowerCase()?.includes('follow') || ((actionOutcome === 'Complete Visit' || actionOutcome === 'Cancel Visit') && nextStep === 'Reschedule')) === true) && (
            <div className="mb-6 p-4 rounded-xl border border-blue-100 bg-blue-50/50" style={{ maxWidth: '100%' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div>
                  <label className="text-sm font-semibold mb-2 block text-muted" style={{ color: '#0369a1' }}>Follow-up In (Days) <span style={{color: 'var(--color-danger)'}}>*</span></label>
                  <input 
                    type="number" 
                    className="input-field w-full" 
                    style={{ backgroundColor: 'white' }}
                    placeholder="e.g. 15"
                    min="0"
                    value={actionDaysToAdd}
                    onChange={(e) => handleActionDaysChange(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold mb-2 block text-muted" style={{ color: '#0369a1' }}>Calculated Follow-up Date</label>
                  <DatePicker
                    selected={actionDate}
                    onChange={handleActionDateChange}
                    className="input-field w-full"
                    wrapperClassName="w-full"
                    style={{ backgroundColor: 'white', width: '100%' }}
                    placeholderText="Select date..."
                    dateFormat="dd/MM/yyyy"
                    minDate={new Date()}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold mb-2 block text-muted" style={{ color: '#0369a1' }}>Follow-up Time <span style={{color: 'var(--color-danger)'}}>*</span></label>
                  <DatePicker
                    wrapperClassName="w-full"
                    selected={getTimeDate(actionTime)}
                    onChange={(date) => {
                      if (date) {
                        const hours = date.getHours().toString().padStart(2, '0');
                        const minutes = date.getMinutes().toString().padStart(2, '0');
                        setActionTime(`${hours}:${minutes}`);
                      } else {
                        setActionTime('');
                      }
                    }}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    className="input-field w-full"
                    style={{ backgroundColor: 'white' }}
                    placeholderText="Select time..."
                    required
                    minTime={
                      actionDate && new Date(actionDate).setHours(0,0,0,0) === new Date().setHours(0,0,0,0)
                        ? new Date()
                        : new Date(new Date().setHours(0, 0, 0, 0))
                    }
                    maxTime={new Date(new Date().setHours(23, 59, 59, 999))}
                  />
                </div>
              </div>
            </div>
          )}
          
          <div className="mb-6">
            <label className="text-sm font-semibold mb-2 block text-muted">Notes (Optional)</label>
            <textarea 
              className="input-field w-full" 
              rows="3" 
              placeholder="Summary of interaction..."
              value={actionNotes}
              onChange={e => setActionNotes(e.target.value)}
              style={{ resize: 'none', borderRadius: '12px' }}
            ></textarea>
          </div>
          <div className="flex gap-4 justify-end">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#10b981', color: 'white' }}>
              Save Action
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TakeActionModal;
